import {
  CUSTOM_PAPER_MAX_MM,
  CUSTOM_PAPER_MIN_MM,
  fromMillimeters,
  PAPER_UNITS,
  type PaperUnit,
  roundMillimeters,
  toMillimeters,
} from '@segnavia/format'
import { useSetting } from './settings'

export type { PaperUnit }

/** How many decimal places a side is written to in each unit: an inch is
 *  large enough that a millimeter is its third place. */
const PLACES: Record<PaperUnit, number> = { mm: 1, in: 3, pt: 1 }

export const PAPER_UNIT_OPTIONS = PAPER_UNITS.map((value) => ({
  value,
  label: value,
}))

function parsePaperUnit(raw: unknown): PaperUnit {
  /* A member of the list is a unit; the cast states what that check proved. */
  return typeof raw === 'string' && PAPER_UNITS.includes(raw as PaperUnit)
    ? (raw as PaperUnit)
    : 'mm'
}

/** The unit a person types a custom paper size in unless they pick another. */
export function usePaperUnit() {
  return useSetting('studio.paperUnit', parsePaperUnit)
}

/** A custom side as typed, in millimeters: a positive number, or nothing. */
export function millimeters(text: string, unit: PaperUnit): number | null {
  const value = Number(text)
  return text.trim() !== '' && Number.isFinite(value) && value > 0
    ? roundMillimeters(toMillimeters(value, unit))
    : null
}

/** Whether a side in millimeters is one Segnavia prints. */
export function printable(mm: number): boolean {
  return mm >= CUSTOM_PAPER_MIN_MM && mm <= CUSTOM_PAPER_MAX_MM
}

/** A side in millimeters, written in a unit without trailing zeros. A bound
 *  is rounded inward, so the number shown is one the bound accepts. */
export function written(
  mm: number,
  unit: PaperUnit,
  round: 'nearest' | 'up' | 'down' = 'nearest',
): string {
  const scale = 10 ** PLACES[unit]
  const step = { nearest: Math.round, up: Math.ceil, down: Math.floor }[round]
  return String(step(fromMillimeters(mm, unit) * scale) / scale)
}
